import { BodyText } from "@/components/ui";
import { useEncounterActiveSession } from "@/features/Charting/network/useEncounterActiveSession";
import { useParams, useSearch } from "@tanstack/react-router";
import { Clock } from "lucide-react";
import { useVisitTimer } from "../hooks/useVisitTimer";

/**
 * Displays the elapsed time since the active primary provider started their
 * current charting session.
 *
 * Renders nothing when the encounter has no active session (released,
 * handed-off, closed, or unassigned). Intended for use in the Chart header
 * row next to the "Visit Note" title.
 *
 * Data source: GET /new-api/encounter/<encounterId>/active-session, which
 * authoritatively tracks session start across take/steal/re-take scenarios.
 */
export const VisitTimer = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });
  const { data } = useEncounterActiveSession({
    patientId,
    encounterId: encounterId?.toString(),
  });
  const elapsedLabel = useVisitTimer(data?.sessionStart, data?.isActive);

  if (!elapsedLabel) {
    return null;
  }

  return (
    <span
      className="flex items-center gap-1"
      aria-label={`Visit time ${elapsedLabel}`}
    >
      <Clock className="size-3 text-tertiary" aria-hidden />
      <BodyText size="xs" textColor="tertiary">
        {elapsedLabel}
      </BodyText>
    </span>
  );
};
